import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Download, CheckCircle, AlertTriangle, TrendingUp, FileText, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResultsPage() {
  const { evaluationId } = useParams();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSuggestion, setExpandedSuggestion] = useState(null);

  useEffect(() => {
    fetchEvaluation();
  }, [evaluationId]);

  const fetchEvaluation = async () => {
    try {
      const response = await axios.get(`${API}/resume/evaluation/${evaluationId}`);
      setEvaluation(response.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load evaluation");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-[#10B981]";
    if (score >= 50) return "text-[#F59E0B]";
    return "text-[#EF4444]";
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return "bg-[#10B981]";
    if (score >= 50) return "bg-[#F59E0B]";
    return "bg-[#EF4444]";
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return "Excellent";
    if (score >= 65) return "Good";
    if (score >= 50) return "Average";
    return "Needs Improvement";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#64748B]">Loading evaluation...</p>
        </div>
      </div>
    );
  }

  if (!evaluation) return null;

  return (
    <div className="min-h-screen bg-white pb-16">
      {/* Header */}
      <Header />
      
      {/* Results Header */}
      <div className="bg-[#F8FAFC] border-b border-slate-200 px-6 md:px-12 lg:px-24 py-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] tracking-tight" data-testid="results-title">
            Resume Evaluation Results
          </h1>
          {evaluation.candidate_name && (
            <p className="text-lg text-[#64748B] mt-2">For: {evaluation.candidate_name}</p>
          )}
          <p className="text-sm text-[#64748B] mt-1">Powered by ElevateX Solutions</p>
        </div>
      </div>

      {/* Main Content - Bento Grid */}
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Overall Score - Large Card */}
            <div className="md:col-span-1">
              <Card className="border-slate-200 shadow-lg h-full" data-testid="overall-score-card">
                <CardContent className="p-8 flex flex-col items-center justify-center h-full">
                  <div className="relative w-48 h-48 mb-6">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="96"
                        cy="96"
                        r="70"
                        stroke="#F1F5F9"
                        strokeWidth="16"
                        fill="none"
                      />
                      <circle
                        cx="96"
                        cy="96"
                        r="70"
                        stroke={evaluation.overall_score >= 80 ? '#10B981' : evaluation.overall_score >= 50 ? '#F59E0B' : '#EF4444'}
                        strokeWidth="16"
                        fill="none"
                        strokeDasharray={`${(evaluation.overall_score / 100) * 440} 440`}
                        strokeLinecap="round"
                        className="score-circle"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className={`text-5xl font-bold ${getScoreColor(evaluation.overall_score)}`} data-testid="overall-score-value">
                        {evaluation.overall_score}
                      </span>
                      <span className="text-sm text-[#64748B] font-medium">out of 100</span>
                    </div>
                  </div>
                  <Badge
                    className={`${getScoreBgColor(evaluation.overall_score)} text-white text-base px-6 py-2`}
                    data-testid="overall-score-badge"
                  >
                    {getScoreLabel(evaluation.overall_score)}
                  </Badge>
                  <h3 className="text-xl font-semibold text-[#0F172A] mt-4">Overall Score</h3>
                </CardContent>
              </Card>
            </div>

            {/* Category Scores */}
            <div className="md:col-span-2">
              <Card className="border-slate-200 shadow-lg" data-testid="category-scores-card">
                <CardHeader>
                  <CardTitle className="text-2xl font-semibold text-[#0F172A]">Category Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="p-8 space-y-6">
                  {evaluation.category_scores.map((category, index) => (
                    <div key={index} className="space-y-2" data-testid={`category-score-${index}`}>
                      <div className="flex justify-between items-center">
                        <span className="text-base font-medium text-[#0F172A]">{category.name}</span>
                        <span className={`text-lg font-bold ${getScoreColor(category.score)}`}>
                          {category.score}/100
                        </span>
                      </div>
                      <Progress
                        value={category.score}
                        className="h-3"
                        data-testid={`category-progress-${index}`}
                      />
                      <p className="text-sm text-[#64748B] leading-relaxed">{category.feedback}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Detailed Feedback Section */}
          <div className="grid md:grid-cols-2 gap-8 mt-8">
            {/* Key Strengths */}
            <Card className="border-slate-200 shadow-lg" data-testid="strengths-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl font-semibold text-[#0F172A]">
                  <CheckCircle className="w-6 h-6 text-[#10B981]" />
                  Key Strengths
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <ul className="space-y-4">
                  {evaluation.key_strengths.map((strength, index) => (
                    <li key={index} className="flex gap-3" data-testid={`strength-${index}`}>
                      <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                      <span className="text-[#0F172A] leading-relaxed">{strength}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Areas for Improvement */}
            <Card className="border-slate-200 shadow-lg" data-testid="improvements-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-2xl font-semibold text-[#0F172A]">
                  <AlertTriangle className="w-6 h-6 text-[#F59E0B]" />
                  Areas for Improvement
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <ul className="space-y-4">
                  {evaluation.areas_for_improvement.map((area, index) => (
                    <li key={index} className="flex gap-3" data-testid={`improvement-${index}`}>
                      <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                      <span className="text-[#0F172A] leading-relaxed">{area}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Actionable Suggestions */}
          <Card className="border-slate-200 shadow-lg mt-8" data-testid="suggestions-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl font-semibold text-[#0F172A]">
                <TrendingUp className="w-6 h-6 text-[#2563EB]" />
                Actionable Suggestions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-4">
                {evaluation.suggestions.map((suggestion, index) => (
                  <div 
                    key={index} 
                    className="border border-slate-200 rounded-lg overflow-hidden"
                    data-testid={`suggestion-${index}`}
                  >
                    <button
                      onClick={() => setExpandedSuggestion(expandedSuggestion === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    >
                      <span className="flex items-center gap-3">
                        <span className="w-8 h-8 bg-[#2563EB] text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                          {index + 1}
                        </span>
                        <span className="text-[#0F172A] font-medium">Suggestion {index + 1}</span>
                      </span>
                      {expandedSuggestion === index ? (
                        <ChevronUp className="w-5 h-5 text-[#64748B]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#64748B]" />
                      )}
                    </button>
                    {expandedSuggestion === index && (
                      <div className="px-6 pb-4 pl-17 text-[#64748B] leading-relaxed border-t border-slate-200 pt-4">
                        {suggestion}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 mt-12 justify-center">
            <button
              onClick={() => navigate("/")}
              className="h-14 px-8 bg-[#2563EB] hover:bg-[#1e40af] text-white text-lg font-semibold rounded-full transition-all hover:scale-105 flex items-center justify-center"
              data-testid="evaluate-another-button"
            >
              <FileText className="w-5 h-5 mr-2" />
              Evaluate Another Resume
            </button>
            <button
              onClick={() => window.print()}
              className="h-14 px-8 border-2 border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white text-lg font-semibold rounded-full transition-all hover:scale-105 flex items-center justify-center"
              data-testid="download-report-button"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Report
            </button>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
}